import React, { forwardRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helpText?: string;
  icon?: React.ReactNode | React.ComponentType<React.SVGProps<SVGSVGElement>>;
  showPasswordToggle?: boolean;
  variant?: 'default' | 'filled' | 'outline';
  size?: 'sm' | 'md' | 'lg';
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helpText,
  icon,
  showPasswordToggle = false,
  variant = 'default',
  size = 'md',
  type = 'text',
  className = '',
  placeholder,
  disabled = false,
  required = false,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(Boolean(props.value || props.defaultValue));

  const actualType = showPasswordToggle && type === 'password' && showPassword ? 'text' : type;
  const isFloatingLabel = Boolean(label) && (variant === 'default' || variant === 'filled');

  const baseClasses = 'block w-full transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    default: `border bg-white focus:ring-2 focus:ring-accent-500 focus:ring-opacity-20 focus:border-accent-500 ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`,
    filled: `border-0 bg-white focus:bg-white focus:ring-2 focus:ring-accent-500 focus:ring-opacity-20 ${error ? 'focus:ring-red-500' : ''}`,
    outline: `border-2 bg-transparent focus:border-accent-500 ${error ? 'border-red-300 focus:border-red-500' : ''}`
  } as const;

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm rounded min-h-[48px]',
    md: 'px-4 py-3 text-base rounded-md min-h-[48px]',
    lg: 'px-6 py-4 text-lg rounded-md min-h-[48px]'
  } as const;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(Boolean(e.target.value));
    props.onChange?.(e);
  };

  // Helper to render icon - supports both component types and elements
  const renderIcon = () => {
    if (!icon) return null;
    
    // If it's a React element (JSX), render it directly
    if (React.isValidElement(icon)) {
      return icon;
    }
    
    // If it's a component type, render it with default props
    if (typeof icon === 'function') {
      const IconComponent = icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;
      return <IconComponent className="h-5 w-5 text-gray-400" />;
    }
    
    return null;
  };

  return (
    <div className="w-full">
      <div className="relative">
        {/* Static Label for non-floating variants */}
        {label && !isFloatingLabel && (
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </label>
        )}

        {/* Input Container */}
        <div className="relative">
          {/* Leading Icon */}
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {renderIcon()}
            </div>
          )}

          {/* Input Field */}
          <input
            ref={ref}
            type={actualType}
            className={`
              ${baseClasses}
              ${variantClasses[variant]}
              ${sizeClasses[size]}
              ${icon ? 'pl-10' : ''}
              ${showPasswordToggle || error ? 'pr-10' : ''}
              ${isFloatingLabel ? 'placeholder-transparent' : ''}
              text-neutral-800 placeholder-neutral-400
              ${className}
            `}
            placeholder={isFloatingLabel ? '' : placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? `${props.id || 'input'}-error` : 
              helpText ? `${props.id || 'input'}-help` : undefined
            }
            {...props}
          />

          {/* Floating Label */}
          {isFloatingLabel && (
            <AnimatePresence>
              <motion.label
                className={`absolute left-4 transition-all duration-200 pointer-events-none ${
                  isFocused || hasValue
                    ? '-top-2 text-xs bg-white px-1 text-primary-500'
                    : 'top-1/2 -translate-y-1/2 text-primary-500/70'
                } ${error && (isFocused || hasValue) ? 'text-red-600' : ''}`}
                initial={{ y: '-50%', scale: 1 }}
                animate={{
                  y: isFocused || hasValue ? '-50%' : '-50%',
                  scale: isFocused || hasValue ? 0.85 : 1,
                  x: isFocused || hasValue ? 0 : 0
                }}
                transition={{ duration: 0.2 }}
              >
                {label}
                {required && <span className="text-red-600 ml-1">*</span>}
              </motion.label>
            </AnimatePresence>
          )}

          {/* Trailing Icons */}
          <div className="absolute inset-y-0 right-0 flex items-center">
            {/* Error Icon */}
            {error && (
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-3" />
            )}

            {/* Password Toggle */}
            {showPasswordToggle && (
              <button
                type="button"
                className="mr-3 p-1 text-primary-500/60 hover:text-primary-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-opacity-20 rounded-lg"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="mt-2 flex items-center text-sm text-red-600"
            id={`${props.id || 'input'}-error`}
          >
            <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help Text */}
      {helpText && !error && (
        <div className="mt-2 text-sm text-gray-600" id={`${props.id || 'input'}-help`}>
          {helpText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input; 